import { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  useVessels, useUpsertVessel, useDeleteVessel,
  useCrew, useUpsertCrew, useDeleteCrew,
  useClients, useUpsertClient, useDeleteClient,
  useVesselAssignments, useCreateAssignment, useWipeDemoData,
  type Vessel, type CrewMember, type Client,
} from "@/hooks/useFleetData";
import { Plus, Pencil, Trash2, AlertTriangle, Database, Ship, Users, Building2, Link2 } from "lucide-react";

const AdminBackend = () => {
  return (
    <AdminLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-teal/80 mb-1">ERP Control</p>
        <h1 className="text-3xl font-bold text-primary-foreground flex items-center gap-3">
          <Database className="w-7 h-7 text-teal" />
          Backend Control Panel
        </h1>
        <p className="text-sm text-primary-foreground/60 mt-2">
          Master data for the entire intranet. Changes here cascade to every connected module.
        </p>
      </div>

      <Tabs defaultValue="vessels" className="space-y-4">
        <TabsList className="bg-navy-deep border border-primary-foreground/10">
          <TabsTrigger value="vessels"><Ship className="w-4 h-4 mr-2" />Vessels</TabsTrigger>
          <TabsTrigger value="crew"><Users className="w-4 h-4 mr-2" />Crew</TabsTrigger>
          <TabsTrigger value="clients"><Building2 className="w-4 h-4 mr-2" />Clients</TabsTrigger>
          <TabsTrigger value="assignments"><Link2 className="w-4 h-4 mr-2" />Assignments</TabsTrigger>
          <TabsTrigger value="danger" className="text-destructive data-[state=active]:text-destructive">
            <AlertTriangle className="w-4 h-4 mr-2" />Danger Zone
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vessels"><VesselsPanel /></TabsContent>
        <TabsContent value="crew"><CrewPanel /></TabsContent>
        <TabsContent value="clients"><ClientsPanel /></TabsContent>
        <TabsContent value="assignments"><AssignmentsPanel /></TabsContent>
        <TabsContent value="danger"><DangerZonePanel /></TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

// ── Vessels ───────────────────────────────────────────────────
const VesselsPanel = () => {
  const { data: vessels = [], isLoading } = useVessels();
  const { data: clients = [] } = useClients();
  const upsert = useUpsertVessel();
  const del = useDeleteVessel();
  const [editing, setEditing] = useState<Partial<Vessel> | null>(null);

  return (
    <Card className="p-5 bg-navy-deep border-primary-foreground/10">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-bold text-primary-foreground">Fleet Registry</h2>
          <p className="text-xs text-primary-foreground/50">{vessels.length} vessels · {vessels.filter(v => v.is_demo).length} demo</p>
        </div>
        <Button onClick={() => setEditing({ name: "", vessel_type: "CTV", status: "available", is_demo: false })} className="bg-teal hover:bg-teal/90">
          <Plus className="w-4 h-4 mr-1" /> Add Vessel
        </Button>
      </div>

      {isLoading ? <p className="text-primary-foreground/50 text-sm">Loading...</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-primary-foreground/10 text-xs uppercase tracking-wider text-primary-foreground/50">
                {["Name", "Type", "IMO", "Flag", "Class", "Status", "Client", "Demo", ""].map(h => (
                  <th key={h} className="text-left py-2 px-2 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vessels.map(v => (
                <tr key={v.id} className="border-b border-primary-foreground/5 hover:bg-primary-foreground/5">
                  <td className="py-2 px-2 font-medium text-primary-foreground">{v.name}</td>
                  <td className="py-2 px-2 text-primary-foreground/70">{v.vessel_type}</td>
                  <td className="py-2 px-2 font-mono text-xs text-primary-foreground/60">{v.imo_number || "—"}</td>
                  <td className="py-2 px-2 text-primary-foreground/70">{v.flag || "—"}</td>
                  <td className="py-2 px-2 text-primary-foreground/70">{v.class_society || "—"}</td>
                  <td className="py-2 px-2"><Badge variant="outline" className="text-[10px]">{v.status}</Badge></td>
                  <td className="py-2 px-2 text-primary-foreground/70 text-xs">
                    {clients.find(c => c.id === v.assigned_client_id)?.name || "—"}
                  </td>
                  <td className="py-2 px-2">{v.is_demo && <Badge className="bg-amber-500/20 text-amber-300 text-[10px]">DEMO</Badge>}</td>
                  <td className="py-2 px-2 flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(v)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => confirm(`Delete ${v.name}?`) && del.mutate(v.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {vessels.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-primary-foreground/40">No vessels yet. Click Add Vessel to start.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Vessel" : "New Vessel"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={editing.name || ""} onChange={e => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Type *</Label>
                <Select value={editing.vessel_type} onValueChange={v => setEditing({ ...editing, vessel_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CTV">CTV</SelectItem>
                    <SelectItem value="AIRCAT 35 Crewliner">AIRCAT 35 Crewliner</SelectItem>
                    <SelectItem value="AIRCAT 35 Combi">AIRCAT 35 Combi</SelectItem>
                    <SelectItem value="USV">USV</SelectItem>
                    <SelectItem value="Support">Support</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>IMO Number</Label><Input value={editing.imo_number || ""} onChange={e => setEditing({ ...editing, imo_number: e.target.value })} /></div>
              <div><Label>Flag</Label><Input value={editing.flag || ""} onChange={e => setEditing({ ...editing, flag: e.target.value })} /></div>
              <div><Label>Class Society</Label><Input value={editing.class_society || ""} onChange={e => setEditing({ ...editing, class_society: e.target.value })} /></div>
              <div><Label>Gross Tonnage</Label><Input value={editing.gross_tonnage || ""} onChange={e => setEditing({ ...editing, gross_tonnage: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={editing.status} onValueChange={v => setEditing({ ...editing, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="on_charter">On Charter</SelectItem>
                    <SelectItem value="standby">Standby</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="in_transit">In Transit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Assigned Client</Label>
                <Select value={editing.assigned_client_id || "none"} onValueChange={v => setEditing({ ...editing, assigned_client_id: v === "none" ? null : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Current Position / Notes</Label>
                <Textarea value={editing.notes || ""} onChange={e => setEditing({ ...editing, notes: e.target.value })} rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => { if (editing) { upsert.mutate(editing as any, { onSuccess: () => setEditing(null) }); } }}>
              Save Vessel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ── Crew ──────────────────────────────────────────────────────
const CrewPanel = () => {
  const { data: crew = [], isLoading } = useCrew();
  const { data: vessels = [] } = useVessels();
  const upsert = useUpsertCrew();
  const del = useDeleteCrew();
  const [editing, setEditing] = useState<Partial<CrewMember> | null>(null);

  return (
    <Card className="p-5 bg-navy-deep border-primary-foreground/10">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-bold text-primary-foreground">Crew Master</h2>
          <p className="text-xs text-primary-foreground/50">{crew.length} crew members</p>
        </div>
        <Button onClick={() => setEditing({ full_name: "", rank: "AB", department: "deck", status: "available", is_demo: false })} className="bg-teal hover:bg-teal/90">
          <Plus className="w-4 h-4 mr-1" /> Add Crew Member
        </Button>
      </div>

      {isLoading ? <p className="text-primary-foreground/50 text-sm">Loading...</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-primary-foreground/10 text-xs uppercase tracking-wider text-primary-foreground/50">
                {["Name", "Rank", "Dept", "Vessel", "Status", "Rotation", ""].map(h => (
                  <th key={h} className="text-left py-2 px-2 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {crew.map(c => (
                <tr key={c.id} className="border-b border-primary-foreground/5 hover:bg-primary-foreground/5">
                  <td className="py-2 px-2 font-medium text-primary-foreground">{c.full_name}</td>
                  <td className="py-2 px-2 text-primary-foreground/70">{c.rank}</td>
                  <td className="py-2 px-2 text-primary-foreground/70 capitalize">{c.department}</td>
                  <td className="py-2 px-2 text-primary-foreground/70 text-xs">{c.current_vessel_name || "—"}</td>
                  <td className="py-2 px-2"><Badge variant="outline" className="text-[10px]">{c.status}</Badge></td>
                  <td className="py-2 px-2 text-xs text-primary-foreground/60">
                    {c.next_rotation_start ? `${c.next_rotation_start} → ${c.next_rotation_end}` : "—"}
                  </td>
                  <td className="py-2 px-2 flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(c)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => confirm(`Delete ${c.full_name}?`) && del.mutate(c.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {crew.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-primary-foreground/40">No crew members yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Crew Member" : "New Crew Member"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full Name *</Label><Input value={editing.full_name || ""} onChange={e => setEditing({ ...editing, full_name: e.target.value })} /></div>
              <div><Label>Rank *</Label><Input value={editing.rank || ""} onChange={e => setEditing({ ...editing, rank: e.target.value })} placeholder="Master, CE, AB..." /></div>
              <div><Label>Department</Label>
                <Select value={editing.department} onValueChange={v => setEditing({ ...editing, department: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deck">Deck</SelectItem>
                    <SelectItem value="engine">Engine</SelectItem>
                    <SelectItem value="catering">Catering</SelectItem>
                    <SelectItem value="shore">Shore-based</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nationality</Label><Input value={editing.nationality || ""} onChange={e => setEditing({ ...editing, nationality: e.target.value })} /></div>
              <div><Label>Current Vessel</Label>
                <Select value={editing.current_vessel_id || "none"} onValueChange={v => {
                  if (v === "none") setEditing({ ...editing, current_vessel_id: null, current_vessel_name: null });
                  else { const ves = vessels.find(x => x.id === v); setEditing({ ...editing, current_vessel_id: v, current_vessel_name: ves?.name || null }); }
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {vessels.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={editing.status} onValueChange={v => setEditing({ ...editing, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="onboard">Onboard</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Email</Label><Input value={editing.email || ""} onChange={e => setEditing({ ...editing, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={editing.phone || ""} onChange={e => setEditing({ ...editing, phone: e.target.value })} /></div>
              <div><Label>Rotation Pattern</Label><Input value={editing.rotation_pattern || ""} onChange={e => setEditing({ ...editing, rotation_pattern: e.target.value })} placeholder="28/28" /></div>
              <div><Label>Passport Expiry</Label><Input type="date" value={editing.passport_expiry || ""} onChange={e => setEditing({ ...editing, passport_expiry: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => { if (editing) upsert.mutate(editing as any, { onSuccess: () => setEditing(null) }); }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ── Clients ───────────────────────────────────────────────────
const ClientsPanel = () => {
  const { data: clients = [], isLoading } = useClients();
  const upsert = useUpsertClient();
  const del = useDeleteClient();
  const [editing, setEditing] = useState<Partial<Client> | null>(null);

  return (
    <Card className="p-5 bg-navy-deep border-primary-foreground/10">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-bold text-primary-foreground">Client Master</h2>
          <p className="text-xs text-primary-foreground/50">{clients.length} clients</p>
        </div>
        <Button onClick={() => setEditing({ name: "", status: "active", is_demo: false })} className="bg-teal hover:bg-teal/90">
          <Plus className="w-4 h-4 mr-1" /> Add Client
        </Button>
      </div>

      {isLoading ? <p className="text-primary-foreground/50 text-sm">Loading...</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-primary-foreground/10 text-xs uppercase tracking-wider text-primary-foreground/50">
                {["Name", "Status", "Region", "Account Mgr", "Contract", "Demo", ""].map(h => (
                  <th key={h} className="text-left py-2 px-2 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id} className="border-b border-primary-foreground/5 hover:bg-primary-foreground/5">
                  <td className="py-2 px-2 font-medium text-primary-foreground">{c.name}</td>
                  <td className="py-2 px-2"><Badge variant="outline" className="text-[10px]">{c.status}</Badge></td>
                  <td className="py-2 px-2 text-primary-foreground/70">{c.region || "—"}</td>
                  <td className="py-2 px-2 text-primary-foreground/70">{c.account_manager || "—"}</td>
                  <td className="py-2 px-2 text-primary-foreground/70 text-xs">{c.contract_ref || "—"}</td>
                  <td className="py-2 px-2">{c.is_demo && <Badge className="bg-amber-500/20 text-amber-300 text-[10px]">DEMO</Badge>}</td>
                  <td className="py-2 px-2 flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(c)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => confirm(`Delete ${c.name}?`) && del.mutate(c.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-primary-foreground/40">No clients yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Client" : "New Client"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name *</Label><Input value={editing.name || ""} onChange={e => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Short Name</Label><Input value={editing.short_name || ""} onChange={e => setEditing({ ...editing, short_name: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={editing.status} onValueChange={v => setEditing({ ...editing, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Region</Label><Input value={editing.region || ""} onChange={e => setEditing({ ...editing, region: e.target.value })} /></div>
              <div><Label>Contract Ref</Label><Input value={editing.contract_ref || ""} onChange={e => setEditing({ ...editing, contract_ref: e.target.value })} /></div>
              <div className="col-span-2"><Label>Account Manager</Label><Input value={editing.account_manager || ""} onChange={e => setEditing({ ...editing, account_manager: e.target.value })} /></div>
              <div><Label>Accent Color</Label><Input type="color" value={editing.accent_color || "#2151A0"} onChange={e => setEditing({ ...editing, accent_color: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => { if (editing) upsert.mutate(editing as any, { onSuccess: () => setEditing(null) }); }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ── Assignments ───────────────────────────────────────────────
const AssignmentsPanel = () => {
  const { data: assignments = [] } = useVesselAssignments();
  const { data: crew = [] } = useCrew();
  const { data: vessels = [] } = useVessels();
  const create = useCreateAssignment();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ status: "scheduled" });

  const submit = () => {
    const c = crew.find(x => x.id === form.crew_member_id);
    const v = vessels.find(x => x.id === form.vessel_id);
    if (!c || !v || !form.rotation_start || !form.rotation_end) return;
    create.mutate({
      crew_member_id: c.id, crew_member_name: c.full_name,
      vessel_id: v.id, vessel_name: v.name,
      rank: c.rank, rotation_start: form.rotation_start, rotation_end: form.rotation_end,
      status: form.status, assigned_by: null, assigned_by_name: null, notes: form.notes || null,
    }, { onSuccess: () => { setOpen(false); setForm({ status: "scheduled" }); } });
  };

  return (
    <Card className="p-5 bg-navy-deep border-primary-foreground/10">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-bold text-primary-foreground">Vessel Assignments</h2>
          <p className="text-xs text-primary-foreground/50">Creating an assignment auto-syncs crew status & schedule</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-teal hover:bg-teal/90">
          <Plus className="w-4 h-4 mr-1" /> New Assignment
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-primary-foreground/10 text-xs uppercase tracking-wider text-primary-foreground/50">
              {["Crew", "Rank", "Vessel", "Start", "End", "Status"].map(h => <th key={h} className="text-left py-2 px-2">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {assignments.map(a => (
              <tr key={a.id} className="border-b border-primary-foreground/5">
                <td className="py-2 px-2 text-primary-foreground">{a.crew_member_name}</td>
                <td className="py-2 px-2 text-primary-foreground/70">{a.rank}</td>
                <td className="py-2 px-2 text-primary-foreground/70">{a.vessel_name}</td>
                <td className="py-2 px-2 text-xs text-primary-foreground/60">{a.rotation_start}</td>
                <td className="py-2 px-2 text-xs text-primary-foreground/60">{a.rotation_end}</td>
                <td className="py-2 px-2"><Badge variant="outline" className="text-[10px]">{a.status}</Badge></td>
              </tr>
            ))}
            {assignments.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-primary-foreground/40">No assignments yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Vessel Assignment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Crew Member</Label>
              <Select value={form.crew_member_id} onValueChange={v => setForm({ ...form, crew_member_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select crew..." /></SelectTrigger>
                <SelectContent>{crew.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name} ({c.rank})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Vessel</Label>
              <Select value={form.vessel_id} onValueChange={v => setForm({ ...form, vessel_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select vessel..." /></SelectTrigger>
                <SelectContent>{vessels.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Rotation Start</Label><Input type="date" value={form.rotation_start || ""} onChange={e => setForm({ ...form, rotation_start: e.target.value })} /></div>
              <div><Label>Rotation End</Label><Input type="date" value={form.rotation_end || ""} onChange={e => setForm({ ...form, rotation_end: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            <p className="text-xs text-primary-foreground/50 bg-teal/5 border border-teal/20 rounded p-2">
              ⚡ This will automatically: create a crew schedule entry, update crew status if rotation is active, and sync vessel/crew across all modules.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit}>Create Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ── Danger Zone ───────────────────────────────────────────────
const DangerZonePanel = () => {
  const wipe = useWipeDemoData();
  const { data: vessels = [] } = useVessels();
  const { data: crew = [] } = useCrew();
  const { data: clients = [] } = useClients();
  const demoCount = vessels.filter(v => v.is_demo).length + crew.filter(c => c.is_demo).length + clients.filter(c => c.is_demo).length;

  return (
    <Card className="p-5 bg-destructive/5 border-destructive/30">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="w-6 h-6 text-destructive flex-shrink-0" />
        <div>
          <h2 className="text-lg font-bold text-destructive">Danger Zone</h2>
          <p className="text-sm text-primary-foreground/70 mt-1">Destructive operations. There is no undo.</p>
        </div>
      </div>

      <div className="space-y-3 mt-4">
        <div className="bg-navy-deep p-4 rounded-lg border border-primary-foreground/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-primary-foreground">Reset to Production</p>
              <p className="text-xs text-primary-foreground/60 mt-1">
                Removes all records tagged as demo data. {demoCount} demo records currently exist.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={demoCount === 0}>Wipe Demo Data</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Wipe all demo data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete {demoCount} demo records (vessels, crew, clients, and their assignments). This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => wipe.mutate()} className="bg-destructive">Yes, wipe it all</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AdminBackend;
